import React, { useEffect, useState } from "react";
import { Button, Header, Icon, Loader, Message, Segment, Table } from "semantic-ui-react";
import { getTransportDocuments, downloadTransportDocument } from "api/backendAPI";

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function validateDocuments(response) {
  return response && response.schema_version === 1 && Array.isArray(response.items)
    && response.items.every(doc => doc && uuid.test(doc.document_id)
      && typeof doc.filename === "string" && doc.filename.length > 0 && doc.filename.length <= 250
      && !/[\\/\r\n\0]/.test(doc.filename)
      && ["original", "derivative"].includes(doc.kind)
      && doc.artifact && ["application/pdf", "image/tiff"].includes(doc.artifact.content_type)
      && /^[a-f0-9]{64}$/.test(doc.artifact.sha256)
      && Number.isInteger(doc.artifact.page_count) && doc.artifact.page_count > 0 && doc.artifact.page_count <= 500
      && Number.isInteger(doc.artifact.byte_size) && doc.artifact.byte_size > 0 && doc.artifact.byte_size <= 67108864
      && typeof doc.source_system === "string"
      && Number.isFinite(Date.parse(doc.received_at)) && Number.isFinite(Date.parse(doc.archived_at))
      && (doc.kind !== "derivative" || (Array.isArray(doc.source_pages)
        && doc.source_pages.length === doc.artifact.page_count
        && doc.source_pages.every(page => Number.isInteger(page) && page > 0 && page <= 500))));
}

export function saveDocumentBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Give the browser time to start the download; never persist a Blob URL.
  window.setTimeout(() => window.URL.revokeObjectURL(url), 10000);
}

function date(value, language) {
  return new Intl.DateTimeFormat(language === "fi" ? "fi-FI" : "en-GB", {
    dateStyle: "short", timeStyle: "short",
  }).format(new Date(value));
}

export default function TransportDocuments({ taskId, reloadKey, t, language }) {
  const [state, setState] = useState({ taskId, loading: true, items: [], error: false });
  const [retry, setRetry] = useState(0);
  const [downloading, setDownloading] = useState(null);
  const [downloadError, setDownloadError] = useState(false);
  useEffect(() => {
    let active = true;
    setState({ taskId, loading: true, items: [], error: false });
    setDownloadError(false);
    getTransportDocuments(taskId).then(response => {
      if (!validateDocuments(response)) throw new Error("invalid document list");
      if (active) setState({ taskId, loading: false, items: response.items, error: false });
    }).catch(() => {
      if (active) setState({ taskId, loading: false, items: [], error: true });
    });
    return () => { active = false; };
  }, [taskId, reloadKey, retry]);

  async function download(doc) {
    setDownloading(doc.document_id);
    setDownloadError(false);
    try {
      const blob = await downloadTransportDocument(taskId, doc.document_id);
      if (!(blob instanceof Blob) || blob.size !== doc.artifact.byte_size || blob.type !== doc.artifact.content_type) {
        throw new Error("invalid document response");
      }
      saveDocumentBlob(blob, doc.filename);
    } catch (_) {
      setDownloadError(true);
    } finally {
      setDownloading(null);
    }
  }

  const loading = state.loading || state.taskId !== taskId;
  return (
    <Segment className="transportDocuments" data-testid="transport-documents">
      <Header as="h2"><Icon name="file alternate outline" />{t("transportDocuments.title")}</Header>
      {loading ? <Loader active inline="centered" content={t("transportDocuments.loading")} />
        : state.error ? <Message negative>
          <p>{t("transportDocuments.loadError")}</p>
          <Button basic onClick={() => setRetry(value => value + 1)} content={t("transportTasks.refresh")} />
        </Message>
          : !state.items.length ? <p className="transportTasks__meta">{t("transportDocuments.empty")}</p>
            : <Table stackable>
              <Table.Header><Table.Row>
                <Table.HeaderCell>{t("transportDocuments.file")}</Table.HeaderCell>
                <Table.HeaderCell>{t("transportDocuments.version")}</Table.HeaderCell>
                <Table.HeaderCell>{t("transportDocuments.source")}</Table.HeaderCell>
                <Table.HeaderCell>{t("transportDocuments.download")}</Table.HeaderCell>
              </Table.Row></Table.Header>
              <Table.Body>{state.items.map(doc => <Table.Row key={doc.document_id}>
                <Table.Cell><strong>{doc.filename}</strong>
                  <div>{t("transportDocuments.pageCount", { count: doc.artifact.page_count })} · {Math.ceil(doc.artifact.byte_size / 1024)} kB</div>
                </Table.Cell>
                <Table.Cell>{t("transportDocuments." + doc.kind)}
                  {doc.kind === "derivative" && <div>{t("transportDocuments.selectedPages")}: {doc.source_pages.join(", ")}</div>}
                </Table.Cell>
                <Table.Cell>{doc.source_system}<div>{date(doc.received_at, language)}</div></Table.Cell>
                <Table.Cell><Button basic icon="download" content={t("transportDocuments.download")}
                  aria-label={t("transportDocuments.download") + " " + doc.filename}
                  disabled={Boolean(downloading)} loading={downloading === doc.document_id}
                  onClick={() => download(doc)} /></Table.Cell>
              </Table.Row>)}</Table.Body>
            </Table>}
      {downloadError && <Message negative>{t("transportDocuments.downloadError")}</Message>}
      {!loading && !state.error && state.items.length > 0 &&
        <p className="transportTasks__sourceTime">{t("transportDocuments.deliveryNote")}</p>}
    </Segment>
  );
}
